import React, { useEffect, useState, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Copy, Trash2, Eye, TrendingUp, Activity, AlertCircle } from 'lucide-react';
import Sidebar from '../Components/Sidebar';
import apiClient from '../Intercepter/APiClient';

const ITEMS_PER_PAGE = 15;

export default function LogsDashboard() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'used_at', direction: 'desc' });

  useEffect(() => {
    apiClient.get('/api/v3/api-log')
      .then(res => {
        if (res.data.success && Array.isArray(res.data.logs)) {
          setLogs(res.data.logs);
        } else {
          console.error('Unexpected response:', res.data);
        }
      })
      .catch(err => {
        console.error('Failed to fetch logs:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    const total = logs.length;
    const success = logs.filter(l => l.response_status < 400).length;
    const errors = logs.filter(l => l.response_status >= 400).length;
    const rateLimited = logs.filter(l => l.response_status === 429).length;
    return { total, success, errors, rateLimited };
  }, [logs]);

  const processedLogs = useMemo(() => {
    let arr = [...logs];

    if (filterStatus !== 'all') {
      const code = Number(filterStatus);
      arr = arr.filter(log => log.response_status === code);
    }

    if (searchText) {
      const q = searchText.toLowerCase();
      arr = arr.filter(log =>
        log.api_key.toLowerCase().includes(q) ||
        log.endpoint.toLowerCase().includes(q) ||
        String(log.user_id).includes(q)
      );
    }

    if (sortConfig.key) {
      arr.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return arr;
  }, [logs, filterStatus, searchText, sortConfig]);

  const pageCount = Math.ceil(processedLogs.length / ITEMS_PER_PAGE);
  const paginatedLogs = processedLogs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const toggleSort = key => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getStatusColor = (status) => {
    if (status < 300) return 'bg-green-500/10 text-green-400 border border-green-500/30';
    if (status < 400) return 'bg-blue-500/10 text-blue-400 border border-blue-500/30';
    if (status === 429) return 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30';
    return 'bg-red-500/10 text-red-400 border border-red-500/30';
  };

  const getMethodColor = (method) => {
    switch(method) {
      case 'GET': return 'bg-blue-500/20 text-blue-300';
      case 'POST': return 'bg-green-500/20 text-green-300';
      case 'PUT': return 'bg-orange-500/20 text-orange-300';
      case 'DELETE': return 'bg-red-500/20 text-red-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="text-center">
            <div className="animate-spin inline-block h-8 w-8 border-4 border-cyan-500 border-t-transparent rounded-full mb-4"></div>
            <p className="text-gray-300">Loading API logs…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="min-h-screen text-white p-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-black mb-2 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 bg-clip-text text-transparent">
              API Usage Dashboard
            </h1>
            <p className="text-gray-400">Monitor and analyze API requests in real-time</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Total Requests', value: stats.total, icon: Activity, color: 'from-cyan-500 to-blue-500' },
              { label: 'Successful', value: stats.success, icon: TrendingUp, color: 'from-green-500 to-emerald-500' },
              { label: 'Errors', value: stats.errors, icon: AlertCircle, color: 'from-red-500 to-pink-500' },
              { label: 'Rate Limited', value: stats.rateLimited, icon: AlertCircle, color: 'from-yellow-500 to-orange-500' }
            ].map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <div key={idx} className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">{stat.label}</p>
                      <p className="text-3xl font-bold">{stat.value}</p>
                    </div>
                    <div className={`bg-gradient-to-br ${stat.color} p-3 rounded-lg`}>
                      <Icon size={24} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Filters & Search */}
          <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search by key, endpoint or user…"
                  value={searchText}
                  onChange={e => { setSearchText(e.target.value); setCurrentPage(1); }}
                  className="w-full pl-10 pr-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-white placeholder:text-gray-500 transition-all"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
              </div>
              <select
                value={filterStatus}
                onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                className="px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-white transition-all"
              >
                <option value="all" className="bg-slate-900">All Statuses</option>
                <option value="200" className="bg-slate-900">200 OK</option>
                <option value="400" className="bg-slate-900">400 Errors</option>
                <option value="429" className="bg-slate-900">429 Rate Limit</option>
                <option value="500" className="bg-slate-900">500+ Server</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    {[
                      { label: 'ID', key: 'id' },
                      { label: 'User', key: 'user_id' },
                      { label: 'API Key', key: 'api_key' },
                      { label: 'Endpoint', key: 'endpoint' },
                      { label: 'Method', key: 'request_method' },
                      { label: 'Status', key: 'response_status' },
                      { label: 'Used At', key: 'used_at' },
                      { label: 'Actions', key: null }
                    ].map(col => (
                      <th
                        key={col.label}
                        onClick={() => col.key && toggleSort(col.key)}
                        className={`px-4 py-3 text-left text-sm font-semibold text-gray-300 ${
                          col.key ? 'cursor-pointer hover:text-cyan-400 transition-colors' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {col.label}
                          {sortConfig.key === col.key && (
                            sortConfig.direction === 'asc'
                              ? <ChevronUp size={14} className="text-cyan-400" />
                              : <ChevronDown size={14} className="text-cyan-400" />
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.map((log) => (
                    <tr key={log.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-300">{log.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-300">{log.user_id}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono">{log.api_key.slice(0, 8)}...</td>
                      <td className="px-4 py-3 text-sm text-cyan-400 hover:text-cyan-300 cursor-pointer">{log.endpoint}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${getMethodColor(log.request_method)}`}>
                          {log.request_method}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(log.response_status)}`}>
                          {log.response_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {new Date(log.used_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 flex gap-2">
                        <button className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors text-blue-300 hover:text-blue-200" title="View">
                          <Eye size={16} />
                        </button>
                        <button className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg transition-colors text-cyan-300 hover:text-cyan-200" title="Copy">
                          <Copy size={16} />
                        </button>
                        <button className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors text-red-300 hover:text-red-200" title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {paginatedLogs.length === 0 && (
              <div className="p-8 text-center">
                <AlertCircle size={48} className="mx-auto text-gray-500 mb-3 opacity-50" />
                <p className="text-gray-400">No logs found matching your criteria</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-400">
              Showing <span className="text-cyan-400 font-bold">{paginatedLogs.length}</span> of <span className="text-cyan-400 font-bold">{processedLogs.length}</span> logs
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-700 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="flex gap-1">
                {[...Array(Math.min(pageCount, 5))].map((_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                          : 'border border-gray-700 hover:border-gray-600 text-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                {pageCount > 5 && <span className="px-2 py-2 text-gray-500">...</span>}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, pageCount))}
                disabled={currentPage === pageCount}
                className="p-2 rounded-lg border border-gray-700 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}